import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Cable,
  ChevronLeft,
  ChevronRight,
  FileText,
  GitBranch,
  Globe,
  History,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Moon,
  Network,
  PhoneCall,
  Radio,
  RadioTower,
  Receipt,
  Route as RouteIcon,
  Settings,
  ShieldAlert,
  ShieldX,
  Sun,
  User,
  BookOpen,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useTheme } from "@/lib/theme.jsx";
import {
  useSidebarCollapsed,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from "@/lib/useSidebarCollapsed";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { HelpModeToggle } from "@/lib/help.jsx";
import { EngineBadge } from "@/components/EngineBadge";

const NAV_ITEMS = [
  {
    groupKey: "traffic",
    items: [
      { to: "/", key: "dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/live", key: "liveTraffic", icon: Radio, testid: "nav-live" },
      { to: "/cdrs", key: "cdrs", icon: FileText, testid: "nav-cdrs" },
      { to: "/billing", key: "billing", icon: Receipt, testid: "nav-billing" },
    ],
  },
  {
    groupKey: "tenants",
    items: [
      { to: "/customers", key: "users", icon: User, testid: "nav-users" },
      // Phase 6o Hotfix H — /allocation nav item retired together with
      // the provider/reseller hierarchy. Flat customer list is the only
      // party view now.
    ],
  },
  {
    groupKey: "policy",
    items: [
      { to: "/trunks", key: "trunks", icon: Cable, testid: "nav-trunks" },
      { to: "/domains", key: "domains", icon: Globe, testid: "nav-domains" },
      { to: "/routing", key: "routing", icon: RouteIcon, testid: "nav-routing" },
      { to: "/acls", key: "acls", icon: ListChecks, testid: "nav-acls" },
    ],
  },
  {
    groupKey: "security",
    items: [
      { to: "/firewall", key: "firewall", icon: ShieldX, testid: "nav-firewall" },
      { to: "/security", key: "securityEvents", icon: ShieldAlert, testid: "nav-security" },
      { to: "/ingress", key: "ingress", icon: RadioTower, testid: "nav-ingress" },
      { to: "/audit", key: "auditLog", icon: History, testid: "nav-audit" },
      { to: "/staff", key: "staff", icon: User, testid: "nav-staff" },
      { to: "/settings", key: "settings", icon: Settings, testid: "nav-settings" },
      { to: "/docs", key: "docs", icon: BookOpen, testid: "nav-docs" },
    ],
  },
];

/** Phase M / N — "My Universe" scope header.
 *
 * Phase O+: simplified back to a plain bolder group label. We no longer
 * render an accent-outlined card here; operators told us the square was
 * too heavy. We just make the MY UNIVERSE section label visually stronger
 * than the other group labels so the scope boundary is still obvious.
 */
function UniverseSectionHeader({ collapsed, t }) {
  if (collapsed) return null;
  return (
    <div
      className="px-5 mb-2 text-[12px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--accent))]"
      data-testid="universe-section-header"
    >
      {t("nav.group.tenants", "MY UNIVERSE")}
    </div>
  );
}

function NavItem({ to, label, Icon, active, onNav, testid, collapsed }) {
  const linkClass = `flex items-center gap-3 rounded-md text-sm transition-colors ${
    collapsed ? "px-2 py-2 justify-center" : "px-3 py-2"
  } ${
    active
      ? "nav-active"
      : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
  }`;
  const link = (
    <Link
      to={to}
      onClick={() => onNav && onNav()}
      data-testid={testid}
      className={linkClass}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </Link>
  );
  if (collapsed) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="text-xs">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return link;
}

function SidebarContent({ onNav, collapsed }) {
  const loc = useLocation();
  const { t } = useTranslation();
  return (
    <nav className="flex flex-col h-full" data-testid="sidebar-nav">
      <div className={`${collapsed ? "px-2" : "px-5"} py-4 border-b border-border`}>
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          <div className="relative shrink-0">
            <div className={`${collapsed ? "h-9 w-9" : "h-11 w-11"} rounded-lg bg-gradient-to-br from-accent/30 to-accent/10 ring-1 ring-accent/40 grid place-items-center shadow-sm`}>
              <Activity className={`${collapsed ? "h-5 w-5" : "h-6 w-6"} text-accent`} />
            </div>
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[hsl(var(--ok))] ring-2 ring-card pulse-live" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div
                className="text-xl font-extrabold tracking-tight bg-gradient-to-r from-[hsl(var(--accent))] via-[hsl(var(--accent))] to-[hsl(var(--info))] bg-clip-text text-transparent leading-none"
                data-testid="brand-name"
              >
                {t("common.appName")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                {t("common.appTagline")}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {NAV_ITEMS.map((g) => (
          <div key={g.groupKey} className="mb-4">
            {!collapsed && g.groupKey !== "tenants" && (
              <div className="px-5 text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                {t(`nav.group.${g.groupKey}`)}
              </div>
            )}
            {g.groupKey === "tenants" && (
              <UniverseSectionHeader collapsed={collapsed} t={t} />
            )}
            <ul className="space-y-0.5 px-2">
              {g.items.map((it) => {
                const active = loc.pathname === it.to;
                return (
                  <li key={it.to}>
                    <NavItem
                      to={it.to}
                      label={t(`nav.${it.key}`)}
                      Icon={it.icon}
                      active={active}
                      onNav={onNav}
                      testid={it.testid}
                      collapsed={collapsed}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <AppVersionFooter collapsed={collapsed} />
    </nav>
  );
}

/** Phase O+ — release stamp shown at the very bottom of the sidebar.
 * Uses the public `/api/version` endpoint. Falls back silently to
 * nothing when the endpoint is unreachable (e.g. during engine reboot).
 */
function AppVersionFooter({ collapsed }) {
  const [stamp, setStamp] = useState("");
  useEffect(() => {
    let alive = true;
    api
      .get("/version")
      .then((r) => alive && setStamp(r.data?.current || ""))
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!stamp) return null;
  if (collapsed) {
    return (
      <div
        className="border-t border-border py-1.5 text-center text-[9px] font-mono text-muted-foreground truncate"
        title={stamp}
        data-testid="app-version-footer-collapsed"
      >
        {stamp.replace(/^v/, "").split("-")[0].slice(0, 6) || "dev"}
      </div>
    );
  }
  return (
    <div
      className="border-t border-border px-5 py-2 text-[10px] font-mono text-muted-foreground truncate"
      title={stamp}
      data-testid="app-version-footer"
    >
      {stamp}
    </div>
  );
}


export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed(false);
  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex">
        {/* desktop sidebar — width animates via inline style so the layout
            recomputes cleanly on orientation change */}
        <aside
          className="hidden md:flex shrink-0 border-r border-border bg-card sticky top-0 h-screen relative transition-[width] duration-200 ease-out"
          style={{ width: sidebarWidth }}
          data-testid="desktop-sidebar"
          data-collapsed={collapsed ? "1" : "0"}
        >
          <SidebarContent collapsed={collapsed} />
          {/* pin/unpin button — always visible at the inner border */}
          <button
            type="button"
            onClick={toggleCollapsed}
            data-testid="sidebar-toggle"
            aria-label={collapsed
              ? t("nav.expandSidebar", "Expand sidebar")
              : t("nav.collapseSidebar", "Collapse sidebar")}
            className="absolute -right-3 top-20 z-20 h-6 w-6 rounded-full border border-border bg-card shadow grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {collapsed
              ? <ChevronRight className="h-3.5 w-3.5" />
              : <ChevronLeft  className="h-3.5 w-3.5" />}
          </button>
        </aside>

        <div className="flex-1 min-w-0">
          {/* top command bar */}
          <header
            className="sticky top-0 z-30 h-[var(--toolbar-h)] border-b border-border bg-background/95 backdrop-blur flex items-center gap-1 sm:gap-2 md:gap-3 px-2 sm:px-4 md:px-6 flex-nowrap overflow-hidden"
            data-testid="top-command-bar"
          >
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden shrink-0" data-testid="open-mobile-nav" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[280px]">
                <SidebarContent onNav={() => setSheetOpen(false)} collapsed={false} />
              </SheetContent>
            </Sheet>
            <div className="flex-1 min-w-0" />
            {/* Engine badge — compact form at all widths; verbose host:port
                only at xl+ via EngineBadge's internal media query. Visible
                from md so iPad operators see engine health at a glance. */}
            <div className="hidden md:block shrink-0">
              <EngineBadge />
            </div>
            {/* Secondary tools — gated to xl (>=1280px) so iPad portrait
                (1024px) keeps the logout button visible. */}
            <div className="hidden xl:block shrink-0">
              <HelpModeToggle />
            </div>
            <div className="hidden xl:block shrink-0">
              <LanguageSwitcher />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="theme-toggle"
              className="shrink-0"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <div className="flex items-center gap-2 pl-2 sm:border-l sm:border-border shrink-0">
              <div className="hidden xl:block text-xs text-muted-foreground">
                <div className="font-medium text-foreground truncate max-w-[120px]">{user?.username}</div>
                {user?.must_change_password && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1 border-[hsl(var(--warn))]/40 text-[hsl(var(--warn))]">
                    Change password
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" className="shrink-0" aria-label={t("nav.logout", "Logout")} title={t("nav.logout", "Logout")}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </header>
          <main className="px-3 sm:px-4 md:px-6 py-4 sm:py-6 max-w-[1600px]">{children}</main>
        </div>
      </div>
    </div>
  );
}
